import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { CalendarCheck, MessageSquareText, FileText, RefreshCcw, Send, Plus, Trash2, X, CheckCircle2, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const TABS = [
  { key: "appointments", label: "Appointments", icon: CalendarCheck },
  { key: "chats", label: "Messages", icon: MessageSquareText },
  { key: "invoices", label: "Invoices", icon: FileText },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("appointments");

  if (loading) return <div className="p-16 text-center text-[#475569]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <div className="flex items-end justify-between border-b border-[#E2E8F0] pb-6">
        <div>
          <div className="text-mono-label">Dashboard</div>
          <h1 className="font-display font-black text-4xl mt-2 tracking-tighter">Ops Console</h1>
        </div>
        <div className="text-mono-label text-[#475569]">{user.email}</div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-5 py-3 flex items-center gap-2 border font-mono text-xs uppercase tracking-widest transition-colors ${tab === t.key ? "bg-brand border-brand text-white" : "border-[#E2E8F0] text-[#475569] hover:border-brand hover:text-brand"}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "appointments" && <Appointments />}
        {tab === "chats" && <Chats />}
        {tab === "invoices" && <Invoices />}
      </div>
    </div>
  );
}

// ----------------------- APPOINTMENTS -----------------------
function Appointments() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/appointments"); setItems(data); } catch {}
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/appointments/${id}`, { status });
      setItems((arr) => arr.map((a) => a.id === id ? data : a));
    } catch {}
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-mono-label">{items.length} total</div>
        <button data-testid="appointments-refresh" onClick={load} className="btn-outline flex items-center gap-2"><RefreshCcw className="w-4 h-4" /> Refresh</button>
      </div>

      <div className="border border-[#E2E8F0]">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#E2E8F0] font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] bg-slate-50">
          <div className="col-span-3">Customer</div>
          <div className="col-span-2">Appliance</div>
          <div className="col-span-3">When</div>
          <div className="col-span-2">City</div>
          <div className="col-span-2">Status</div>
        </div>
        {busy && <div className="p-8 text-center text-[#475569]">Loading…</div>}
        {!busy && items.length === 0 && <div className="p-8 text-center text-[#475569]">No appointments yet.</div>}
        {items.map((a) => (
          <div key={a.id} data-testid={`appt-row-${a.id}`} className="border-b border-[#E2E8F0]">
            <div className="grid grid-cols-12 gap-2 px-4 py-4 items-center hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded(expanded === a.id ? null : a.id)}>
              <div className="col-span-3">
                <div className="font-semibold">{a.customer_name}</div>
                <div className="text-xs text-[#475569]">{a.phone}</div>
              </div>
              <div className="col-span-2 text-sm">{a.appliance}{a.brand ? ` · ${a.brand}` : ""}</div>
              <div className="col-span-3 text-sm">{a.preferred_date} · {a.preferred_time}</div>
              <div className="col-span-2 text-sm">{a.city}</div>
              <div className="col-span-2"><StatusChip s={a.status} /></div>
            </div>
            {expanded === a.id && (
              <div className="px-4 pb-5 pt-1 bg-slate-50 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <div className="text-mono-label">Problem</div>
                  <p className="mt-2 text-sm text-[#0F172A]">{a.problem}</p>
                  <div className="text-mono-label mt-4">Address</div>
                  <p className="mt-2 text-sm">{a.address}, {a.city}</p>
                  <div className="text-mono-label mt-4">Email</div>
                  <p className="mt-2 text-sm">{a.email}</p>
                </div>
                <div className="flex flex-col gap-2">
                  {["scheduled", "in_progress", "completed", "cancelled"].map((s) => (
                    <button key={s} data-testid={`appt-set-${s}-${a.id}`} onClick={(e) => { e.stopPropagation(); setStatus(a.id, s); }} className="btn-outline text-xs">
                      Mark {s.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusChip({ s }) {
  const map = {
    requested: "border-[#94A3B8] text-[#475569]",
    scheduled: "border-blue-500 text-blue-400",
    in_progress: "border-yellow-500 text-yellow-400",
    completed: "border-emerald-500 text-emerald-400",
    cancelled: "border-red-500 text-red-400",
    paid: "border-emerald-500 text-emerald-400",
    sent: "border-blue-500 text-blue-400",
    draft: "border-[#94A3B8] text-[#475569]",
    void: "border-red-500 text-red-400",
  };
  return <span className={`chip ${map[s] || ""}`}>{s}</span>;
}

// ----------------------- CHATS -----------------------
function Chats() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/chats"); setSessions(data); } catch {}
    setBusy(false);
  };
  const loadThread = async (sid) => {
    try { const { data } = await api.get(`/chat/${sid}`); setThread(data); } catch {}
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (active) loadThread(active.session_id); }, [active]);
  useEffect(() => {
    if (!active) return;
    const iv = setInterval(() => loadThread(active.session_id), 10000);
    return () => clearInterval(iv);
  }, [active]);

  const send = async () => {
    if (!reply.trim() || !active) return;
    try {
      await api.post("/admin/chats/reply", { session_id: active.session_id, message: reply });
      setReply("");
      loadThread(active.session_id);
      load();
    } catch {}
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="border border-[#E2E8F0] max-h-[600px] overflow-y-auto">
        <div className="p-3 border-b border-[#E2E8F0] flex items-center justify-between">
          <div className="text-mono-label">Sessions ({sessions.length})</div>
          <button data-testid="chats-refresh" onClick={load} className="text-[#475569] hover:text-brand"><RefreshCcw className="w-4 h-4" /></button>
        </div>
        {busy && <div className="p-6 text-center text-[#475569] text-sm">Loading…</div>}
        {sessions.map((s) => (
          <button
            key={s.session_id}
            data-testid={`chat-session-${s.session_id}`}
            onClick={() => setActive(s)}
            className={`w-full text-left p-4 border-b border-[#E2E8F0] hover:bg-slate-50 transition-colors ${active?.session_id === s.session_id ? "bg-white/70" : ""}`}
          >
            <div className="flex items-center justify-between">
              <div className="font-semibold">{s.name}</div>
              <span className="font-mono text-[10px] text-[#94A3B8]">{new Date(s.last_at).toLocaleDateString()}</span>
            </div>
            <div className="text-xs text-[#475569] mt-1 truncate">{s.last_message}</div>
            <div className="text-[10px] font-mono text-[#94A3B8] mt-1">{s.phone || s.email || "—"}</div>
          </button>
        ))}
      </div>

      <div className="md:col-span-2 border border-[#E2E8F0] flex flex-col max-h-[600px]">
        {!active && <div className="flex-1 flex items-center justify-center text-[#94A3B8]">Select a conversation</div>}
        {active && (
          <>
            <div className="p-4 border-b border-[#E2E8F0]">
              <div className="font-display font-bold text-lg">{active.name}</div>
              <div className="text-xs text-[#475569]">{active.phone} · {active.email || "no email"}</div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {thread.map((m) => (
                <div key={m.id} className={`flex ${m.from_owner ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] px-3 py-2 text-sm ${m.from_owner ? "bg-brand text-white" : "bg-[#E2E8F0] text-slate-900"}`}>
                    {m.message}
                    <div className="text-[10px] opacity-70 mt-1 font-mono">
                      {new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-[#E2E8F0] p-3 flex items-center gap-2">
              <input data-testid="chat-reply-input" className="field flex-1" placeholder="Reply…" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
              <button data-testid="chat-reply-send" onClick={send} className="btn-brand"><Send className="w-4 h-4" /></button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ----------------------- INVOICES -----------------------
function Invoices() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/invoices"); setItems(data); } catch {}
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-mono-label">{items.length} invoices</div>
        <button data-testid="invoices-new" onClick={() => setShowForm(true)} className="btn-brand"><Plus className="w-4 h-4" /> New Invoice</button>
      </div>

      <div className="border border-[#E2E8F0]">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#E2E8F0] font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] bg-slate-50">
          <div className="col-span-2">Number</div>
          <div className="col-span-3">Customer</div>
          <div className="col-span-2">Total</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-3">Actions</div>
        </div>
        {busy && <div className="p-8 text-center text-[#475569]">Loading…</div>}
        {!busy && items.length === 0 && <div className="p-8 text-center text-[#475569]">No invoices yet.</div>}
        {items.map((inv) => (
          <div key={inv.id} data-testid={`invoice-row-${inv.id}`} className="grid grid-cols-12 gap-2 px-4 py-4 items-center border-b border-[#E2E8F0]">
            <div className="col-span-2 font-mono text-sm">{inv.number}</div>
            <div className="col-span-3">
              <div>{inv.customer_name}</div>
              <div className="text-xs text-[#475569]">{inv.email}</div>
            </div>
            <div className="col-span-2 font-display font-bold">${inv.total.toFixed(2)}</div>
            <div className="col-span-2"><StatusChip s={inv.status} /></div>
            <div className="col-span-3">
              <a href={`/invoice/${inv.id}`} target="_blank" rel="noreferrer" className="btn-outline text-xs inline-flex items-center gap-2">
                View <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        ))}
      </div>

      {showForm && <InvoiceForm onClose={() => setShowForm(false)} onCreated={(inv) => { setItems((a) => [inv, ...a]); setShowForm(false); }} />}
    </div>
  );
}

function InvoiceForm({ onClose, onCreated }) {
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "", address: "",
    tax_rate: 0, notes: "",
    line_items: [{ description: "Service call — appliance diagnosis", quantity: 1, unit_price: 100 }],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateLine = (i, k, v) => setForm((f) => {
    const li = [...f.line_items];
    li[i] = { ...li[i], [k]: v };
    return { ...f, line_items: li };
  });
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", quantity: 1, unit_price: 0 }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

  const subtotal = form.line_items.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0);
  const tax = subtotal * (Number(form.tax_rate) || 0) / 100;
  const total = subtotal + tax;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = {
        ...form,
        tax_rate: Number(form.tax_rate) || 0,
        line_items: form.line_items.map((li) => ({
          description: li.description,
          quantity: Number(li.quantity) || 1,
          unit_price: Number(li.unit_price) || 0,
        })),
      };
      const { data } = await api.post("/invoices", payload);
      onCreated(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
      <div className="bg-surface border border-[#E2E8F0] w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0] sticky top-0 bg-surface z-10">
          <div>
            <div className="text-mono-label">New</div>
            <h3 className="font-display font-black text-2xl">Create Invoice</h3>
          </div>
          <button data-testid="invoice-form-close" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label><div className="text-mono-label">Customer name *</div><input required data-testid="inv-customer" className="field mt-1" value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} /></label>
            <label><div className="text-mono-label">Email *</div><input required type="email" data-testid="inv-email" className="field mt-1" value={form.email} onChange={(e) => update("email", e.target.value)} /></label>
            <label><div className="text-mono-label">Phone</div><input data-testid="inv-phone" className="field mt-1" value={form.phone} onChange={(e) => update("phone", e.target.value)} /></label>
            <label><div className="text-mono-label">Address</div><input data-testid="inv-address" className="field mt-1" value={form.address} onChange={(e) => update("address", e.target.value)} /></label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-mono-label">Line items</div>
              <button type="button" data-testid="inv-add-line" onClick={addLine} className="btn-outline text-xs"><Plus className="w-3 h-3" /> Add line</button>
            </div>
            <div className="space-y-2">
              {form.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input placeholder="Description" data-testid={`inv-line-desc-${i}`} className="field col-span-6" value={li.description} onChange={(e) => updateLine(i, "description", e.target.value)} />
                  <input placeholder="Qty" type="number" min="0" step="0.5" data-testid={`inv-line-qty-${i}`} className="field col-span-2" value={li.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} />
                  <input placeholder="Price" type="number" min="0" step="0.01" data-testid={`inv-line-price-${i}`} className="field col-span-3" value={li.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)} />
                  <button type="button" data-testid={`inv-line-remove-${i}`} onClick={() => removeLine(i)} disabled={form.line_items.length === 1} className="col-span-1 text-red-400 hover:text-red-300 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label><div className="text-mono-label">Tax rate (%)</div><input type="number" min="0" step="0.001" data-testid="inv-tax" className="field mt-1" value={form.tax_rate} onChange={(e) => update("tax_rate", e.target.value)} /></label>
            <label className="md:col-span-2"><div className="text-mono-label">Notes</div><input data-testid="inv-notes" className="field mt-1" value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="e.g. Whirlpool WRF555 fridge — compressor swap" /></label>
          </div>

          <div className="border-t border-[#E2E8F0] pt-4 flex flex-col items-end text-sm font-mono">
            <div>Subtotal: <span className="font-display font-bold">${subtotal.toFixed(2)}</span></div>
            <div>Tax: <span className="font-display font-bold">${tax.toFixed(2)}</span></div>
            <div className="text-lg mt-1">Total due: <span className="font-display font-black text-brand" data-testid="inv-total">${total.toFixed(2)}</span></div>
          </div>

          {err && <div className="text-red-400 text-sm border border-red-900 px-4 py-3">{err}</div>}

          <div className="flex items-center justify-end gap-3 border-t border-[#E2E8F0] pt-4">
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
            <button data-testid="inv-submit" disabled={busy} className="btn-brand"><CheckCircle2 className="w-4 h-4" /> {busy ? "Sending…" : "Create & Email"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
